import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { PersistOptions } from 'zustand/middleware';
import type { User } from '../types/auth';
import { fetchUserInfo } from '@/api/auth';
import { NAVIGATION, SESSION_TIMEOUT, SESSION_NAME } from '@/config/routes';

// 세션 스토리지에 저장할 상태의 타입 정의
type AuthPersist = {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  lastActivity: number;
};

interface AuthState {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  lastActivity: number;
  sessionTimeoutId: number | null;
  updateActivity: () => void;
  checkSessionTimeout: () => void;
  me: (token: string) => Promise<boolean>;
  logout: () => void;
  logoutAndRedirect: () => string; // 로그아웃 후 리다이렉트 경로 반환
  clearError: () => void;
}

// 세션 스토리지 사용을 위한 persist 설정
const persistConfig: PersistOptions<AuthState, AuthPersist> = {
  name: SESSION_NAME,
  storage: {
    getItem: (name) => {
      // 세션 스토리지에서 데이터 가져오기
      const value = sessionStorage.getItem(name);
      return value ? JSON.parse(value) : null;
    },
    setItem: (name, value) => {
      // 세션 스토리지에 데이터 저장
      sessionStorage.setItem(name, JSON.stringify(value));
    },
    removeItem: (name) => {
      // 세션 스토리지에서 데이터 삭제
      sessionStorage.removeItem(name);
    }
  },
  partialize: (state) => ({
    user: state.user,
    token: state.token,
    isAuthenticated: state.isAuthenticated,
    lastActivity: state.lastActivity
  })
};

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      token: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,
      lastActivity: Date.now(),
      sessionTimeoutId: null,

      // 사용자 활동 시간 업데이트 및 세션 타임아웃 체크 설정
      updateActivity: () => {
        const state = get();
        const now = Date.now();
        
        // 마지막 활동 시간 업데이트
        console.log('[Auth] 사용자 활동 감지: 마지막 활동 시간 업데이트');
        set({ lastActivity: now });
        
        // 이미 타임아웃 체커가 설정되어 있다면 초기화
        if (state.sessionTimeoutId !== null) {
          window.clearTimeout(state.sessionTimeoutId);
        }
        
        // 새로운 타임아웃 체커 설정
        const timeoutId = window.setTimeout(() => {
          get().checkSessionTimeout();
        }, SESSION_TIMEOUT);
        
        set({ sessionTimeoutId: timeoutId });
      },
      
      // 세션 타임아웃 체크
      checkSessionTimeout: () => {
        const state = get();
        const now = Date.now();
        const timeSinceLastActivity = now - state.lastActivity;
        
        console.log(`[Auth] 세션 타임아웃 체크: 마지막 활동 후 ${Math.floor(timeSinceLastActivity / 1000)}초 경과`);
        
        if (state.isAuthenticated && timeSinceLastActivity >= SESSION_TIMEOUT) {
          console.log('[Auth] 세션 타임아웃: 30분 동안 활동이 없어 로그아웃합니다.');
          
          // 세션 스토리지 직접 삭제
          sessionStorage.removeItem(SESSION_NAME);
          
          // 로그아웃 및 리다이렉트 경로 결정
          const redirectPath = get().logoutAndRedirect();
          
          // 페이지 이동
          window.location.href = redirectPath;
        }
      },

      me: async (token: string) => {
        set({ isLoading: true, error: null });
        
        try {
          // 새로운 API 서비스 사용
          const response = await fetchUserInfo(token);
          
          if (response.code === 200) {
            const now = Date.now();
            set({
              user: response.data,
              token,
              isAuthenticated: true,
              isLoading: false,
              error: null,
              lastActivity: now
            });
            
            // 로그인 성공 시 활동 업데이트 및 세션 타임아웃 체크 설정
            get().updateActivity();
            
            // cafe24 플랫폼인 경우 샵 목록 가져오기
            if (response.data.platform === 'cafe24') {
              try {
                // 동적 import로 순환 의존성 방지
                const { useShopStore } = await import('./shop');
                const { fetchShopList } = useShopStore.getState();
                await fetchShopList();
              } catch (error) {
                console.error('[Auth] 샵 목록 가져오기 오류:', error);
              }
            }
            
            return true;
          } else {
            set({
              isLoading: false,
              error: response.message || '인증에 실패했습니다.'
            });
            return false;
          }
        } catch (error) {
          let errorMessage = '인증 중 오류가 발생했습니다.';
          if (error instanceof Error) {
            errorMessage = error.message || errorMessage;
          }
          set({ isLoading: false, error: errorMessage });
          return false;
        }
      },

      logout: () => {
        // 타임아웃 체커가 있다면 제거
        const state = get();
        if (state.sessionTimeoutId !== null) {
          window.clearTimeout(state.sessionTimeoutId);
        }
        
        // 샵 정보 초기화
        try {
          import('./shop').then(({ useShopStore }) => {
            const { clearShops } = useShopStore.getState();
            clearShops();
          });
        } catch (error) {
          console.error('[Auth] 샵 정보 초기화 오류:', error);
        }
        
        set({ 
          user: null, 
          token: null, 
          isAuthenticated: false,
          sessionTimeoutId: null 
        });
      },
      
      // 로그아웃 후 플랫폼에 따라 리다이렉트 경로 반환
      logoutAndRedirect: () => {
        const state = get();
        // 현재 플랫폼 정보 저장 - 로그아웃 전에 반드시 저장해야 함
        const currentPlatform = state.user?.platform;
        const platformMallId = state.user?.platformMallId;
        console.log('[Auth] 현재 플랫폼:', currentPlatform, 'platformMallId:', platformMallId);
        
        // 기본 리다이렉트 경로
        let baseRedirectPath = NAVIGATION.DEFAULT_AFTER_LOGOUT;
        
        // URL 객체 생성하여 파라미터 추가
        const redirectUrl = new URL(baseRedirectPath, window.location.origin);
        
        // 플랫폼 정보가 있으면 파라미터로 추가
        if (currentPlatform) {
          redirectUrl.searchParams.set('platform', currentPlatform);
        }
        
        if (platformMallId) {
          redirectUrl.searchParams.set('platform_mall_id', platformMallId);
        }
        
        const finalRedirectPath = redirectUrl.pathname + redirectUrl.search;
        console.log('[Auth] 최종 리다이렉트 경로:', finalRedirectPath);
        
        // 로그아웃 실행 - 경로 결정 후에 실행
        get().logout();
        
        return finalRedirectPath;
      },

      clearError: () => {
        set({ error: null });
      },
      

    }),
    persistConfig
  )
);
